import * as Color from "color"
import PptxGenJS, {
	ChartBarDataset,
	ChartOptions ,
	HTMLToPowerPointOptions,
	ChartPieDataset,
	PlaceholderOptions,
	ChartData
} from "pptxgenjs"
import * as React from "react"
import * as ReactDOM from "react-dom"
import { IntlProvider } from "react-intl"
import DebtTable, { Decomposition } from "./debt-table"
import { getChartBaseOptions, getChartSecondOfTwoOptions, getChartFirstOfTwoOptions, getChartColors } from "../pptx-configuration/charts"
import { MasterSlide } from "../pptx-configuration/master-slides"
import {
	getPlaceholderChartBodyOptions,
	Placeholder
} from "../pptx-configuration/placeholders"
import * as Conf from "../../configuration"
import * as CommonUtils from "../utils"
import * as BankDomain from "../../../../domain/sfp/bank-domain"
import * as LoanDomain from "../../../../domain/sfp/loan-domain"
import * as PpiDomain from "../../../../domain/sfp/ppi-domain"
import * as Model from "../../../../model"
import { ColorsHelper } from "../../../../util/colors"

export function createGlobalChartOptions(baseColor: Color): ChartOptions {
	return {
		...getChartBaseOptions(getPlaceholderChartBodyOptions()),
		barGrouping: "stacked",
		chartColors: ["003f5c", "32bac9", "a3172a"],
		legendPos: "b",
		showTitle: false
	}
}

export function createChartData(
	decomposition: Decomposition,
	decomposedLoans: Model.LoanWithDecomposition[],
	years: number[],
	localAuthority: Model.LocalAuthority,
	loans:any,
	scenario:Model.Scenario,
	ppi?:Model.Ppi
): [ChartBarDataset, ChartBarDataset, ChartBarDataset] {
	if (localAuthority.isDebtSimplified && ppi) {
		return handleSimplifiedDebt(decomposition, decomposedLoans, years, localAuthority, loans, scenario, ppi);
	}
	
	return decomposedLoans.reduce<[ChartBarDataset, ChartBarDataset,ChartBarDataset]>(
		([realDatasetCapital,realDatasetInterest, simulatedDataset], decomposedLoan) => {
			years.forEach((year, yearIndex) => {
				const value = getValue(decomposition, decomposedLoan.decomposition, year)

				if (decomposedLoan.loan.simulate){
					simulatedDataset.values[yearIndex] += value
				}
				else{
					if(decomposition == Decomposition.Annuity){
						realDatasetCapital.values[yearIndex] += getValue(Decomposition.CapitalReimbursement, decomposedLoan.decomposition, year);
						realDatasetInterest.values[yearIndex]+= getValue(Decomposition.InterestReimbursement, decomposedLoan.decomposition, year);
					}else if(decomposition == Decomposition.CapitalReimbursement){
						realDatasetCapital.values[yearIndex] += value;
					}else if(decomposition == Decomposition.InterestReimbursement){
						realDatasetInterest.values[yearIndex] += value;
					}
					else{
						realDatasetCapital.values[yearIndex] += value;
					}
				}
			})
			return [realDatasetCapital,realDatasetInterest, simulatedDataset]
		}, [initDataset(false,"capital", years),initDataset(false,"interest",years), initDataset(true,"simulated", years)]
	)
}

function handleSimplifiedDebt(
	decomposition: Decomposition,
	decomposedLoans: Model.LoanWithDecomposition[],
	years: number[],
	localAuthority: Model.LocalAuthority,
	loans:any,
	scenario:Model.Scenario,
	ppi:Model.Ppi
): [ChartBarDataset, ChartBarDataset, ChartBarDataset] {
	
	const capital = initDataset(false, "capital", years);
	const interest = initDataset(false, "interest", years);
	const simulated = initDataset(true, "simulated", years);
	const currentYear = new Date().getFullYear();
	const account66111 = localAuthority.accounts.find((o:any) => o.account.number === '66111');
	// Remboursement prospectif : même source que le référentiel (limité aux années du PPI)
	const ppiYearLoans = ppi ? PpiDomain.getPpiYearLoans(decomposedLoans, ppi) : [];
	// Emprunts simulés : repris de la ligne « Total dette simulée » de la dette détaillée.
	// `decomposedLoans` est pré-filtré par createDebtSection quand l'option
	// « inclure les emprunts simulés » est décochée — la série tombe alors à 0,
	// comme en mode dette détaillée.
	const simulatedTotals = LoanDomain.getSimulatedLoansTotalsByYearFromDecomposed(decomposedLoans, years);

	years.forEach((year, index) => {
		let valueCapital = 0;
		let valueInterest = 0;
		let valueSimulated = 0;
		
		const simplifiedYear = localAuthority.localAuthorityDebtSimplifiedYears
			? localAuthority.localAuthorityDebtSimplifiedYears.find(o => o.year === year)
			: null;

		const crdEnCoursDeLaDette = simplifiedYear && simplifiedYear.amount != null ? simplifiedYear.amount : 0;
		const simulatedYear = LoanDomain.getSimulatedLoanTotals(simulatedTotals, year);
		const crdEmpruntsSimules = simulatedYear.remainingCapital;
		const interetsEmpruntsSimules = simulatedYear.interest;
		const remboursementEmpruntsSimules = simulatedYear.reimboursedCapital;

		const isRetrospective = year < currentYear; 
		if (isRetrospective) {
			// Rétrospective — intérêts depuis les observations (compte 66111)
			const retro = localAuthority && localAuthority.observations ? localAuthority.observations.find((o) => o.account && o.account.number === '66111' && o.year === year) : null;
			valueInterest = retro ? (retro.amount != null ? Number(retro.amount.toFixed(2)) : 0) : 0;
		} else {
			// Prospective : intérêts depuis les décompositions réelles (emprunts contractuels)
			const prospective = loans ? loans.find((o: any) => o.year === year) : null;
			if (prospective != null) {
				valueInterest = prospective.interests != null ? prospective.interests : 0;
			}
			// Surcharge par le scénario (compte 66111) si une valeur a été saisie
			const yearData = scenario && scenario.years ? scenario.years.find((o: any) => o.year === year) : null;
			if (yearData && yearData.accounts && yearData.accounts.length > 0 && account66111) {
				const data = yearData.accounts.find((o: any) => o.receipts === account66111.account.receipts && o.chapter_id === account66111.chapter.id);
				if (data && data.amount != null) {
					valueInterest = data.amount;
				}
			}
		}

		let valueRemboursementEmpruntsReels = 0;
		
		if (isRetrospective) {
			// Rétrospective — remboursement depuis les observations (compte 1641)
			const retro = localAuthority.observations ? localAuthority.observations.find((o) => o.account && o.account.number === '1641' && o.year === year && o.receipts === false) : null;
			valueRemboursementEmpruntsReels = retro ? (retro.amount != null ? Number(retro.amount.toFixed(2)) : 0) : 0;
		} else {
			// Prospective : ppi.loanDebtAmount si saisi, sinon remboursement réel limité aux années du PPI (comme le référentiel)
			const ppiYear = ppi.years.find((o: any) => o.year === year);
			const prospective = ppiYearLoans.find((o: any) => o.year === year);
			const prospectiveSimplifiee = ppiYear && ppiYear.loanDebtAmount != null ? ppiYear.loanDebtAmount : null;
			valueRemboursementEmpruntsReels = prospectiveSimplifiee != null
				? prospectiveSimplifiee
				: prospective && prospective.reimbourse != null ? Number(prospective.reimbourse.toFixed(2)) : 0;
		}	

		if(decomposition == Decomposition.Annuity){
			valueCapital = valueRemboursementEmpruntsReels;
			valueInterest = valueInterest;
			valueSimulated = interetsEmpruntsSimules + remboursementEmpruntsSimules;
		} else if(decomposition == Decomposition.CapitalReimbursement){
			valueCapital = valueRemboursementEmpruntsReels;
			valueInterest = 0;
			valueSimulated = remboursementEmpruntsSimules;
		} else if(decomposition == Decomposition.InterestReimbursement){
			valueCapital = 0;
			valueInterest = valueInterest;
			valueSimulated = interetsEmpruntsSimules;
		} else {
			// RemainingCapital
			valueCapital = crdEnCoursDeLaDette;
			valueInterest = 0;
			valueSimulated = crdEmpruntsSimules;
		}

		capital.values[index] = valueCapital;
		interest.values[index] = valueInterest;
		simulated.values[index] = valueSimulated;
		
	});
	return [capital, interest, simulated];
}

export function createPerBankChartData(
	decomposedLoans: Model.LoanWithDecomposition[],
	decomposition: "annuity" | "interest" | "remaining" | "reimbursed",
	year: number
): [ChartPieDataset] {
	const banks = LoanDomain.distinctBanks(decomposedLoans.map(d => d.loan))

	return [banks.reduce<ChartPieDataset>((dataset, bank, bankIndex) => {
		dataset.labels.push(bank ? bank.label : "NC")
		dataset.values.push(0)

		for (const decomposedLoan of decomposedLoans) {
			if (decomposedLoan.loan.simulate) continue
			if (!BankDomain.equal(bank, decomposedLoan.loan.bank)) continue

			const value =
			decomposition === "annuity" ? LoanDomain.getAnnuity(
				decomposedLoan.decomposition,
				year
			) :
			decomposition === "interest" ? LoanDomain.getYearInterest(
				decomposedLoan.decomposition,
				year
			) :
			decomposition === "reimbursed" ? LoanDomain.getYearReimboursedCapital(
				decomposedLoan.decomposition,
				year
			) :
			decomposition === "remaining" ? LoanDomain.getYearRemainingCapital(
				decomposedLoan.decomposition,
				year
			) : 0

			dataset.values[bankIndex] += value
		}

		return dataset
	}, { labels: [], name: "Par banque", values: [] })]
}

export function createPieChartData(
	decomposedLoans: Model.LoanWithDecomposition[],
	decomposition: "annuity" | "interest" | "remaining" | "reimbursed",
	year: number
): [ChartPieDataset] {

	const labels = ["Dette normale", "Dette normale recuperable","Dette simulee", "Dette simulee recuperable"]

	return [decomposedLoans.reduce<ChartPieDataset>((dataset, decomposedLoan) => {

			const value =
			decomposition === "annuity" ? LoanDomain.getAnnuity(
				decomposedLoan.decomposition,
				year
			) :
			decomposition === "interest" ? LoanDomain.getYearInterest(
				decomposedLoan.decomposition,
				year
			) :
			decomposition === "reimbursed" ? LoanDomain.getYearReimboursedCapital(
				decomposedLoan.decomposition,
				year
			) :
			decomposition === "remaining" ? LoanDomain.getYearRemainingCapital(
				decomposedLoan.decomposition,
				year
			) : 0

			let val = []
                
			if(!decomposedLoan.loan.detteRecuperable  && !decomposedLoan.loan.simulate) dataset.values[1] += value
			if(decomposedLoan.loan.detteRecuperable  && !decomposedLoan.loan.simulate) dataset.values[2] += value
			if(!decomposedLoan.loan.detteRecuperable  && decomposedLoan.loan.simulate) dataset.values[3] += value
			if(decomposedLoan.loan.detteRecuperable  && decomposedLoan.loan.simulate) dataset.values[4] += value
			dataset.values.forEach(r=>{
                 if(!isNaN(r)){
                    val.push(r)
				 }
			} )
			dataset.values = val
			
		return dataset
	}, { labels, name: "Dette", values: [0,0,0,0] })]
}

export function createPieChartOptions(
	data: [ChartPieDataset],
	placeholderBodyOptions: PlaceholderOptions,
	baseColor: Color
): ChartOptions {
	const options: ChartOptions =  { ...getChartBaseOptions(placeholderBodyOptions) }
	options.title = "Dette"
	options.chartColors =["B2CB3B","5F8949","F05B06","BB2307"],
	options.showPercent = true;
	options.dataLabelFontBold=true;
	return options
}

export function createPerBankChartOptions(
	data: [ChartPieDataset],
	coupled: boolean,
	placeholderBodyOptions: PlaceholderOptions,
	baseColor: Color
): ChartOptions {
	const options: ChartOptions = coupled
	? { ...getChartFirstOfTwoOptions(placeholderBodyOptions) }
	: { ...getChartBaseOptions(placeholderBodyOptions) }
	options.title = "Par banque"
	options.chartColors = getChartColors()
	options.legendFontSize = 11;
	options.showPercent = true;
	options.dataLabelFontBold=true;

	return options
}

export function createPerRateTypeChartData(
	decomposedLoans: Model.LoanWithDecomposition[],
	decomposition: "annuity" | "interest" | "remaining" | "reimbursed",
	year: number
): [ChartPieDataset] {
	const labels = ["Taux fixe", "Taux variable"]
	return [decomposedLoans.reduce<ChartPieDataset>((dataset, decomposedLoan) => {
		const value =
		decomposition === "annuity" ? LoanDomain.getAnnuity(
			decomposedLoan.decomposition,
			year
		) :
		decomposition === "interest" ? LoanDomain.getYearInterest(
			decomposedLoan.decomposition,
			year
		) :
		decomposition === "reimbursed" ? LoanDomain.getYearReimboursedCapital(
			decomposedLoan.decomposition,
			year
		) :
		decomposition === "remaining" ? LoanDomain.getYearRemainingCapital(
			decomposedLoan.decomposition,
			year
		) : 0

		if (decomposedLoan.loan.variableRate) dataset.values[1] += value
		else dataset.values[0] += value

		return dataset
	}, { labels, name: "Par taux", values: [0, 0] })]
}

export function createPerRateTypeChartOptions(
	coupled: boolean,
	placeholderBodyOptions: PlaceholderOptions,
	baseColor: Color
): ChartOptions {
	const options = coupled
	? { ...getChartSecondOfTwoOptions(placeholderBodyOptions) }
	: { ...getChartBaseOptions(placeholderBodyOptions) }
		options.title = "Par taux"
		options.chartColors = getChartColors()
		options.legendFontSize = 11;
		options.showPercent = true;
		options.dataLabelFontBold=true;

	return options
}

export function createYears(
	conf: Conf.ReportConfiguration,
	decomposedLoans: Model.LoanWithDecomposition[],
	localAuthority?: Model.LocalAuthority,
	ppi?: Model.Ppi,
	scenario?: Model.Scenario
): number[] {
	const currentYear = new Date().getFullYear()

	const decompositions = decomposedLoans.map(lwd => lwd.decomposition)
	const start = conf.debt.enableRetrospective
	? conf.generalities.years[0]
	: currentYear

	let end = currentYear - 1
	if (conf.debt.enableProspective) {
		if (localAuthority && localAuthority.isDebtSimplified) {
			// Dette simplifiée : max de toutes les sources
			const ppiMaxYear = ppi && ppi.years && ppi.years.length > 0
				? Math.max(...ppi.years.map((y: any) => y.year))
				: null
			const simplifiedMaxYear = localAuthority.localAuthorityDebtSimplifiedYears && localAuthority.localAuthorityDebtSimplifiedYears.length > 0
				? Math.max(...localAuthority.localAuthorityDebtSimplifiedYears.map(y => y.year))
				: null
			// Intérêts : scénario (compte 66111) en surcharge, décomposition réelle en base
			const scenarioMaxYear = scenario && scenario.years && scenario.years.length > 0
				? Math.max(...scenario.years.map((y: any) => y.year))
				: null
			const lastPaymentDate = decompositions.length > 0 ? LoanDomain.findLastPaymentDate(decompositions) : null
			const loansMaxYear = lastPaymentDate ? lastPaymentDate.getFullYear() : null
			const candidates = [ppiMaxYear, simplifiedMaxYear, scenarioMaxYear, loansMaxYear].filter(y => y != null) as number[]
			if (candidates.length > 0) end = Math.max(...candidates)
		} else {
			// Dette normale : utiliser la dernière date de paiement des emprunts
			if (decompositions.length > 0) {
				const lastPaymentDate = LoanDomain.findLastPaymentDate(decompositions)
				if (lastPaymentDate) {
					end = lastPaymentDate.getFullYear()
				}
			}
		}
	}

	return CommonUtils.createYears(start, end)
}

export function includeDebtTable(
	pptx: PptxGenJS,
	slidesTitle: string,
	tableBaseId: string,
	decomposition: Decomposition,
	decomposedLoans: Model.LoanWithDecomposition[],
	years: number[],
	conf: Conf.ReportConfiguration
): void {
	const maxColumns = 10

	const tablesCount = years.length > maxColumns ?
	Math.round(years.length / maxColumns) :
	1

	for (let i = 0; i < tablesCount; i++) {
		const tempDiv = document.createElement("div")
		document.head.appendChild(tempDiv)

		const tableId = `${tableBaseId}-${i}`
		const subYearStart = i * maxColumns
		const subYears = years.slice(subYearStart, subYearStart + maxColumns)
		const table = <IntlProvider locale={navigator.language} defaultLocale="fr">
			<DebtTable
			currency={conf.currency}
			decomposition={decomposition}
			id={tableId}
			locale={conf.locale}
			decomposedLoans={decomposedLoans}
			roundness={conf.generalities.roundness}
			tableConfiguration={conf.table}
			years={subYears} />
		</IntlProvider>

		ReactDOM.render(table, tempDiv, () => {
			const slidesOptions: HTMLToPowerPointOptions = {
				addText: {
					text: slidesTitle,
					opts: { placeholder: Placeholder.Title }
				},
				addHeaderToEach: true,
				master: MasterSlide.Table
			}
			pptx.addSlidesForTable(tableId, slidesOptions)
			document.head.removeChild(tempDiv)
		})
	}
}

function initDataset(
	simulated: boolean,
	type: "annuity" | "interest" | "capital" | "simulated",
	years: number[]
): ChartBarDataset {
	const labels = years.map(y => y.toString())
	let name = "";
	if(simulated){
		name = "Emprunts simulés";
	}else{
		if(type=="annuity"){
			name ="Emprunts contractés";
		}else if(type=="capital"){
			name ="Emprunts contractés (Capital)";
		}else{
			name ="Emprunts contractés (Intérêts)";
		}
	}
	const values = years.map(() => 0)
	return { labels, name, values }
}

function getValue(
	decomposition: Decomposition,
	loanDecomposition: Model.LoanDecomposition,
	year: number
): number {
	switch (decomposition) {
		case Decomposition.Annuity:
			return LoanDomain.getAnnuity(loanDecomposition, year)
		case Decomposition.CapitalReimbursement:
			return LoanDomain.getYearReimboursedCapital(loanDecomposition, year)
		case Decomposition.InterestReimbursement:
			return LoanDomain.getYearInterest(loanDecomposition, year)
		case Decomposition.RemainingCapital:
			return LoanDomain.getYearRemainingCapital(loanDecomposition, year)
	}
}


// WEBPACK FOOTER //
// ./src/component/management-report/generator/debt-generator/utils.tsx